// Story 8.7 (ADO-2016) — working from a previous order, on the expanded order card.
//
// The Re-add band (Figma 4594:13736, 4925:50633) closes the card: "Add order to cart"
// (AC1, AC2) and "Replace cart with order" (AC3), each behind its confirm modal
// (4757:13799, 4757:13811). Both work on the whole order at the ordered quantities, so
// the line table above is read-only.
//
// The server decides what goes into the cart (D-4), so nothing here is optimistic. What
// landed is confirmed by the app-wide toast (Figma 5943:31353 "Order added to cart",
// 5943:31545 "Cart replaced"): the lines added, the order reference and the order's total.
// A Banner inside the card reports everything else: every line that was not added, by
// product name (AC4), nothing added at all, a failure, or a busy cart.

import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Banner, Button, Modal, useHeaderLayout } from '../../design-system';
import type {
  AddFromPreviousOrderRequest,
  PreviousOrderLineNotAdded,
} from '../../api/basket.client';
import { useBasketStore, type AddFromOrderOutcome } from '../../basket/useBasketStore';
import { MAX_LINE_QUANTITY } from '../../basket/lineQuantity';
import { formatMoney, type MoneyValue } from '../../shared/money';
import { useToastStore } from '../../toast/useToastStore';
import styles from './OrderHistory.module.css';

type Confirming = 'ADD' | 'REPLACE' | null;

/**
 * The Re-add band and its two confirm modals. `ready` is false until the order's lines
 * have loaded, and stays false when no line has a positive quantity (D-6 ignores such
 * lines, so there is nothing to add); both actions are disabled then, and while any
 * basket write is in flight (the store would refuse it anyway).
 */
export function ReAddBand({
  orderId,
  reference,
  total,
  ready,
  cartName,
}: {
  orderId: string;
  reference: string;
  /** The order's total as sent, which the toast quotes. */
  total: MoneyValue;
  ready: boolean;
  /** Story 8.3 (ADO-2012), card 07: the cart's name as the page shows it ("Ambient"), when
   *  known. */
  cartName?: string;
}) {
  const addFromOrder = useBasketStore((s) => s.addFromOrder);
  const inFlight = useBasketStore((s) => s.addingFromOrder || s.clearing || s.pending.size > 0);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<AddFromOrderOutcome | null>(null);
  // Story 8.3 (ADO-2012), card 07 (D-21): a replace refused because the cart changed since it
  // was opened; the dialog then asks again with the cart's line count now.
  const [changedLines, setChangedLines] = useState<number | null>(null);
  // ADO-1779: on mobile both confirms are bottom sheets with the confirm action on top
  // (Figma 5415:23457, 5415:23747); tablet and desktop keep the centred dialog.
  const mobile = useHeaderLayout() === 'mobile';
  const placement = mobile
    ? ({ placement: 'bottom', sheetGutter: 'compact' } as const)
    : ({ placement: 'center' } as const);

  // The card unmounts on a depot switch or page step, possibly with a request in flight;
  // its answer must not then settle state here (`OrderCard`'s `aliveRef` discipline).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const disabled = !ready || inFlight || submitting;

  const submit = async (request: AddFromPreviousOrderRequest) => {
    if (submitting) return;
    setSubmitting(true);
    setOutcome(null);
    const result = await addFromOrder(orderId, request);
    // Before the alive check: the cart did change, so the toast confirms it even when the
    // card is gone by now.
    if (result.kind === 'added' && result.added.length > 0) {
      useToastStore
        .getState()
        .showToast(addedToast(request.mode, result.added.length, reference, total));
    }
    if (!aliveRef.current) return;
    setSubmitting(false);
    if (result.kind === 'changed') {
      // Nothing was replaced: the dialog stays and asks again; the store already shows the
      // cart as it is, and a confirm sends the revision just shown.
      setChangedLines(result.lineCount);
      return;
    }
    setChangedLines(null);
    setConfirming(null);
    setOutcome(result);
  };

  // A confirmed request is on its way: the modal waits for it rather than closing on a
  // cart that has not changed yet. Cancel after a refusal only closes it (D-21).
  const close = () => {
    if (submitting) return;
    setConfirming(null);
    setChangedLines(null);
  };

  return (
    <>
      <div className={styles.reAddBand}>
        <Button
          variant="primary"
          className={styles.reAddOrder}
          disabled={disabled}
          onClick={() => setConfirming('ADD')}
        >
          Add order to cart
        </Button>
        <Button
          variant="secondary"
          className={styles.reAddReplace}
          disabled={disabled}
          onClick={() => setConfirming('REPLACE')}
        >
          Replace cart with order
        </Button>
      </div>
      <ReAddResult outcome={outcome} />

      <Modal
        open={confirming === 'ADD'}
        onClose={close}
        title="Add this order to your cart?"
        {...placement}
        footer={
          <ConfirmActions
            confirmFirst={mobile}
            cancel={
              <Button variant="secondary" onClick={close} aria-disabled={submitting || undefined}>
                Cancel
              </Button>
            }
            confirm={
              <Button
                variant="primary"
                onClick={() => void submit({ mode: 'ADD' })}
                aria-disabled={submitting || undefined}
              >
                Add to cart
              </Button>
            }
          />
        }
      >
        {/* The frames end this sentence "Items already in your cart won't be affected",
            which is not what the server does: an item already in the cart has these
            quantities added to it. The copy keeps saying what happens. */}
        <p className={styles.modalBody}>
          The lines from order <strong>{reference}</strong> will be added to your current cart at
          today&apos;s prices. Where an item is already in your cart, these quantities are added to
          it.
        </p>
      </Modal>

      <Modal
        open={confirming === 'REPLACE'}
        onClose={close}
        title={
          changedLines === null ? 'Replace your cart with this order?' : 'Your cart has changed'
        }
        {...placement}
        footer={
          <ConfirmActions
            confirmFirst={mobile}
            cancel={
              <Button variant="secondary" onClick={close} aria-disabled={submitting || undefined}>
                Cancel
              </Button>
            }
            confirm={
              <Button
                variant="destructive"
                // D-7: the one place `confirm: true` is sent from.
                onClick={() => void submit({ mode: 'REPLACE', confirm: true })}
                aria-disabled={submitting || undefined}
              >
                Replace cart
              </Button>
            }
          />
        }
      >
        {changedLines === null ? (
          /* Body copy with the frames' strong runs, desktop and tablet (4757:13811,
             5416:24617) and mobile (5415:23757) alike. */
          <p className={styles.modalBody}>
            Your current cart will be <strong>cleared and replaced</strong> with the lines from
            order <strong>{reference}</strong> at today&apos;s prices.{' '}
            <strong>This can&apos;t be undone.</strong>
          </p>
        ) : (
          // Story 8.3 (ADO-2012), card 07: the frames' copy, word for word (DV-3). An alert, so the
          // switch from the usual question is announced (a changed title alone is not).
          <p className={styles.modalBody} role="alert">
            {`Your ${cartName === undefined ? '' : `${cartName} `}cart changed since you opened this. It now has ${linesCount(changedLines)}. Replace it with order ${reference} anyway? This can't be undone.`}
          </p>
        )}
      </Modal>
    </>
  );
}

/** The confirm footer's two actions. Cancel leads in the centred dialog (Figma 4757:13799);
 *  the mobile sheet stacks the confirm on top (5415:23457). Ordered in the DOM rather than
 *  with CSS, so the tab order follows what is drawn. */
function ConfirmActions({
  confirmFirst,
  cancel,
  confirm,
}: {
  confirmFirst: boolean;
  cancel: ReactNode;
  confirm: ReactNode;
}) {
  // Keyed, so a live switch across 768px with the dialog open MOVES the two buttons rather
  // than relabelling them in place (which would leave focus on what became the other one).
  const actions = [
    <Fragment key="cancel">{cancel}</Fragment>,
    <Fragment key="confirm">{confirm}</Fragment>,
  ];
  return <>{confirmFirst ? actions.reverse() : actions}</>;
}

/** "1 line" / "N lines", card 07's count. */
function linesCount(count: number): string {
  return `${count} ${count === 1 ? 'line' : 'lines'}`;
}

function itemCount(count: number): string {
  return `${count} ${count === 1 ? 'item' : 'items'}`;
}

/** The success toast's copy: the lines this request added, from which order, and that
 *  order's total as sent. */
function addedToast(
  mode: AddFromPreviousOrderRequest['mode'],
  addedCount: number,
  reference: string,
  total: MoneyValue,
): { title: string; description: string } {
  const lines = `${addedCount} ${addedCount === 1 ? 'line' : 'lines'} from order ${reference} · ${formatMoney(total)}`;
  return mode === 'REPLACE'
    ? { title: 'Cart replaced', description: `Now ${lines}` }
    : { title: 'Order added to cart', description: lines };
}

/** The result Banner (Task 6.4). A dropped answer (the depot changed) shows nothing, and
 *  neither does a clean success: the toast confirms that one. */
function ReAddResult({ outcome }: { outcome: AddFromOrderOutcome | null }) {
  // Story 8.3 (ADO-2012): a refused replace is told in its dialog (card 07), not here.
  if (outcome === null || outcome.kind === 'dropped' || outcome.kind === 'changed') return null;
  if (outcome.kind === 'failed') {
    return (
      <div className={styles.reAddResult}>
        <Banner type="error" title="The order was not added to your cart">
          {outcome.message}
        </Banner>
      </div>
    );
  }
  if (outcome.kind === 'busy') {
    return (
      <div className={styles.reAddResult}>
        <Banner type="info" title="Your cart is still updating">
          Please try again in a moment.
        </Banner>
      </div>
    );
  }

  const title =
    outcome.added.length === 0
      ? 'No items were added to your cart.'
      : `${itemCount(outcome.added.length)} added to your cart.`;
  if (outcome.notAdded.length === 0) {
    if (outcome.added.length > 0) return null;
    return (
      <div className={styles.reAddResult}>
        <Banner type="info" title={title} />
      </div>
    );
  }
  const overLimit = outcome.notAdded.filter((line) => line.reason === 'QUANTITY_LIMIT');
  // Story 8.3 (ADO-2012), D-23 (card 08): the cart line changed since the Shopper saw it.
  const changed = outcome.notAdded.filter((line) => line.reason === 'CHANGED_IN_CART');
  const unavailable = outcome.notAdded.filter(
    (line) => line.reason !== 'QUANTITY_LIMIT' && line.reason !== 'CHANGED_IN_CART',
  );
  return (
    <div className={styles.reAddResult}>
      <Banner type="warning" title={title}>
        {unavailable.length > 0 && (
          <NotAddedList
            heading={`${itemCount(unavailable.length)} from this order ${
              unavailable.length === 1 ? 'is' : 'are'
            } no longer available and ${unavailable.length === 1 ? 'was' : 'were'} not added:`}
            lines={unavailable}
          />
        )}
        {overLimit.length > 0 && (
          <NotAddedList
            heading={`${itemCount(overLimit.length)} would pass the most one line can hold (${MAX_LINE_QUANTITY.toLocaleString(
              'en-IE',
            )}) and ${overLimit.length === 1 ? 'was' : 'were'} not added:`}
            lines={overLimit}
          />
        )}
        {changed.length > 0 && (
          // Our words, after card 08's (D-26): each with the cart's quantity now.
          <NotAddedList
            heading={`${itemCount(changed.length)} changed in your cart while you were looking and ${
              changed.length === 1 ? 'was' : 'were'
            } not added, so nothing was doubled:`}
            lines={changed}
            label={(line) =>
              line.currentQuantity === undefined
                ? productLabel(line)
                : `${productLabel(line)} (now ${line.currentQuantity})`
            }
          />
        )}
      </Banner>
    </div>
  );
}

/** One group of refused lines, as a sentence: the Banner's supporting text is phrasing
 *  content, so the product names are listed inline rather than as a list. */
function NotAddedList({
  heading,
  lines,
  label = productLabel,
}: {
  heading: string;
  lines: readonly PreviousOrderLineNotAdded[];
  label?: (line: PreviousOrderLineNotAdded) => string;
}) {
  return (
    <span className={styles.notAdded}>
      {heading} {lines.map(label).join(', ')}.
    </span>
  );
}

/** A refused line's product, by name, else by code. */
function productLabel(line: PreviousOrderLineNotAdded): string {
  return line.productName ?? line.skuCode;
}
